import React, { useCallback, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { carouselPhotos } from '../data/site';
const ease = [0.22, 1, 0.36, 1] as const;
export function PhotoCarousel() {
  const [index, setIndex] = useState(0);
  const total = carouselPhotos.length;
  const goTo = useCallback((next: number) => setIndex((next + total) % total), [total]);
  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowLeft') goTo(index - 1);
    if (event.key === 'ArrowRight') goTo(index + 1);
  };
  const current = carouselPhotos[index];
  return <section aria-label="Photo gallery" className="w-full bg-ivory py-16 sm:py-24 lg:py-32" data-id="element-235" data-mp="246xboke9kekr">
      <div className="mx-auto w-full max-w-[1240px] px-4 sm:px-8 lg:px-12" data-id="element-236" data-mp="1gh32cct5l859">
        <div role="group" aria-roledescription="carousel" tabIndex={0} onKeyDown={onKeyDown} className="relative overflow-hidden rounded-xl bg-sand-light outline-none transition-[aspect-ratio] duration-500 ease-calm focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-4 focus-visible:ring-offset-ivory" style={{
        aspectRatio: current.ratio
      }} data-id="element-237" data-mp="1gezzutatdmjn">
          <AnimatePresence initial={false} mode="sync" data-id="element-238" data-mp="3jxy92po3qh5l">
            <motion.img key={current.src} src={current.src} alt={current.alt} draggable={false} className="absolute inset-0 h-full w-full select-none object-cover" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.4,
            ease
          }} drag="x" dragConstraints={{
            left: 0,
            right: 0
          }} dragElastic={0.12} onDragEnd={(_, info) => {
            if (info.offset.x < -60) goTo(index + 1);
            if (info.offset.x > 60) goTo(index - 1);
          }} data-id="element-239" data-mp="3jzj38e6i7y2x" />
          </AnimatePresence>

          <button type="button" onClick={() => goTo(index - 1)} aria-label="Previous photograph" className="group absolute left-3 top-1/2 -translate-y-1/2 rounded-full border border-white/40 bg-white/25 p-2.5 text-white/90 backdrop-blur-md transition-all duration-300 ease-calm hover:scale-105 hover:bg-white/45 hover:text-white sm:left-6 sm:p-3.5" data-id="element-240" data-mp="27krpozds3hta">
            <ChevronLeft className="h-4 w-4 transition-transform duration-300 ease-calm group-hover:-translate-x-0.5 sm:h-5 sm:w-5" strokeWidth={1.25} aria-hidden="true" data-id="element-241" data-mp="1oujsexwrfll0" />
          </button>

          <button type="button" onClick={() => goTo(index + 1)} aria-label="Next photograph" className="group absolute right-3 top-1/2 -translate-y-1/2 rounded-full border border-white/40 bg-white/25 p-2.5 text-white/90 backdrop-blur-md transition-all duration-300 ease-calm hover:scale-105 hover:bg-white/45 hover:text-white sm:right-6 sm:p-3.5" data-id="element-242" data-mp="1xtezec7mozpd">
            <ChevronRight className="h-4 w-4 transition-transform duration-300 ease-calm group-hover:translate-x-0.5 sm:h-5 sm:w-5" strokeWidth={1.25} aria-hidden="true" data-id="element-243" data-mp="3llns240kqvsw" />
          </button>
        </div>

        <div className="mt-6 flex items-center justify-center" data-id="element-244" data-mp="4bzhpdakngbi">
          <p className="sr-only" aria-live="polite" data-id="element-245" data-mp="4aep3t7ti7ga">
            Photograph {index + 1} of {total}
          </p>

          <ul className="flex items-center gap-2" data-id="element-246" data-mp="3qjpmtm7d4jk7">
            {carouselPhotos.map((photo, i) => <li key={photo.src} data-id="element-247" data-mp="3viaegx8jtux1" data-mpi={i}>
                <button type="button" onClick={() => goTo(i)} aria-label={`Go to photograph ${i + 1}`} aria-current={i === index} className={`h-1.5 rounded-full transition-all duration-300 ease-calm ${i === index ? 'w-5 bg-forest-deep/45' : 'w-1.5 bg-forest-deep/18 hover:bg-forest-deep/30'}`} data-id="element-248" data-mp="3vh8zwisbqqak" />
              </li>)}
          </ul>
        </div>
      </div>
    </section>;
}